// websocket/zone-entry-truth.ts — MV2 S2 (built by MV4, owner-authorized 2026-09-28): the page half of a walk's PERSISTENT-ZONE ENTRIES.
//
// The legacy walk loop fires janus_try_persistent_aoe_entry_for_creature() at the foot a creature newly overlaps a zone. A client-truth walk
// (movement-truth.ts) that makes such an entry now resolves it here instead of handing the whole walk back:
//   PLAN  (synchronous, BEFORE the walk commits — every hand-back decided on state, before a die is seen):
//         · zones/walk-zone-crossings.ts already proved the entry and its first-cut rules (the mover ends inside the zone, the walk strips
//           nothing, no painted / prismatic / trap / empowered zone, not a truncated walk);
//         · the entry can never HALT or RE-PRICE the walk: no status it can apply is a legacy halt (dead, prone, restrained, unconscious,
//           grappled, paralyzed, stunned) or carries a speed / movement layer — so the walk's committed route and price stand whatever the
//           dice say (the legacy loop halts, and restores the step, only on those);
//         · the frame (janus-engine/ability/paoe-entry.ts, oracle-proven) runs on synthetic extreme dice, then the REAL draw, with the entering
//           creature at its entry foot; a frame the page cannot resolve hands the whole walk back before anything is saved.
//   COMMIT (after the walk's movement commit is accepted): each stored verdict in foot order through the SHARED persistence — the ability
//         lane's own commit (ability-truth.ts commitZoneEntryVerdict → ability_truth_commit_inc.php `zone_entry`, owner 2026-09-29): rows,
//         columns, statuses, removals, stacks, charges, PC columns, the death / concentration tails and the browser's per-turn entry record.
//         A verdict refused on stale state is re-resolved by the PAGE on its current rows. Then one exit commit
//         strips the walk's rows and what each walk-through entry (and a caster outside its zone) was given (the legacy end-of-move order).
// Registered with movement-truth.ts (registerZoneEntryPlanner) so this module can use the ability lane's readers without an import cycle.
import { isSelfStatusOrigin } from '../janus-engine/statuses/status-origin.js';
import { paoeEntryContext, type PaoeEntryVerdict } from '../janus-engine/ability/paoe-entry.js';
import { hasSurfaceTypeValue, combinePaoeEntryRecords, PAOE_ENTRY_RECORD, runPaoeEntryOnce, type PaoeEntryOnceVerdict } from '../janus-engine/ability/paoe-entry-once.js';
import { readPaoeEntryRecords, readPaoeEntryRecords as readLegacyPaoeEntryRecords, readSurfaceEntryRecords, onlySurfaceEntryZones, ensurePaoeEntryRecords, adoptPaoeEntryRecords, forgetPaoeEntryRecords } from './paoe-entry-records.js';
import { payloadDryRunRollSource } from '../janus-engine/ability/environmental-payload.js';
import { HandBack, Refusal, type JanusCtx, type LogRow } from '../janus-engine/ability/context.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { recordingRollSource, resumableRollSource, tapedRollSource, type RecordedDraw } from '../janus-engine/actions/roll-source.js';
import { applyMovementTruth, type MovementTruthRow } from '../janus-engine/movement/client-truth-commit.js';
import type { PlainWalkResult } from '../janus-engine/movement/plain-walk.js';
import { abilityTruthReaders, commitZoneEntryVerdict, verdictParticipants, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { adoptChainRevs, dryRunChain, survivorsAfterReresolve, type ChainRevs } from '../janus-engine/movement/compound-entry-chain.js';
import { upsertStatusOnRow } from './time-truth.js';
import { actorEncounterFor, holdCommittedZoneStrips, MovementRefused, ZoneRecordsNeeded, registerZoneEntryPlanner, waitForActorMovement, type ZoneEntryPlanner } from './movement-truth.js';
import { missingDefinitionId } from './ability-preflight-needs.js';
import { fetchAndCacheObject } from './object-cache.js';
import { commitZoneExits, creatureStandsInZone, registerZoneExitCommitter, resolveWalkZones, zoneAnchorRow, type ZoneExitOutcome } from './zone-truth.js';
import { statusRows, statusItemId, statusRemovalBefore } from '../janus-engine/statuses/expiry.js';
import { statusScalar } from '../janus-engine/actions/activity.js';
import { prepareDeathResources } from '../shared/spawn-browser.js';
import { deathSourceIds } from '../janus-engine/death/compose.js';
import { holdsTaggedSummon } from '../janus-engine/death/summons.js';
import { readItemField } from '../janus-engine/compile/item-field-reader.js';
import { ecLean } from '../janus-engine/ability/context.js';
import { askTriggerConsent, createTriggerConsent, planWithConsent, type PageTriggerConsent, offerFortunate } from './trigger-consent.js';
import { consentPending, TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { encounterField, posCoord } from '../shared/array-types.js';
import { resolveZoneExit } from '../janus-engine/zones/zone-exit.js';
import { hydrateRemovalSupportDefinitions } from '../janus-engine/statuses/removal-support.js';
import { exactRemovalTailDefinitionIds } from '../janus-engine/statuses/exact-removal-tails.js';
import { exploreDryPatterns, pinnedDryEnv } from '../janus-engine/statuses/gm-dry-run.js';
import { refuseUnported } from '../janus-engine/unported.js';
import type { ZoneStrip } from '../janus-engine/zones/walk-zone-crossings.js';
import { movementDecisionView, finishMovementDecision } from '../janus-engine/movement/decision-view.js';

type Row = Record<string, unknown>;
type Entry = NonNullable<PlainWalkResult['zone_entries']>[number];
const row = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/** move_encounter_creature_logic.php's post-entry halts (the step is restored and the walk stops). */
const HALT_STATUS_IDS = new Set(['9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA', 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', 'E34AF232-5438-88FC-9A9F-73850CCCFD51',
  '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B', 'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1', '0C2A8037-B0BC-8300-C818-198881B00E7D',
  'BF31EE0A-5B3C-370B-0118-96B067CFD929', 'D218F4FB-5693-87B9-93C1-776FC1F7ED90',
  // Immobile: every speed 0 — the loop's `speed <= 0` halt (mv4_zone_halt_oracle H2 once the encumbrance pass stopped stripping it)
  'F40E19A3-FC86-187F-050E-8C8B69761353']);
const APPLIED_LAYERS = ['status_effect_applied_to_target_after_cast', 'status_effect_applied_to_target_on_hit',
  'status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_caster_after_cast'];
type DeathRead = NonNullable<JanusCtx['read']['deathResources']>;
const deathReaders = new WeakMap<Row, { signature: string; read: DeathRead }>();
const deathFlights = new WeakMap<Row, { signature: string; loading: Promise<void> }>();

/** Encounter objects are patched in place. Bind packages to current member identities and source roots. */
function deathSignature(encounter: Row): string {
  const read = abilityTruthReaders({}, encounter, new Set());
  return JSON.stringify([...members(encounter)].map(([id, member]) => {
    const own = String(readItemField(member, 'item_id') ?? ecLean(member, 'item_id') ?? '');
    let sources: string[];
    try { sources = deathSourceIds(member, read); } catch { sources = []; }
    return [id, own, String(readItemField(member, 'item_type') ?? ecLean(member, 'item_type') ?? ''),
      sources.map(source => [source, !!read.definition(source)]), !!read.definition(own)];
  }));
}

/** The synchronous walk planner resumes after source packages are held, as it does for entry records. */
function ensureDeathReaders(encounter: Row): Promise<void> | null {
  const signature = deathSignature(encounter);
  if (deathReaders.get(encounter)?.signature === signature) return null;
  const pending = deathFlights.get(encounter);
  if (pending?.signature === signature) return pending.loading;
  const all = members(encounter);
  const loading = prepareDeathResources([...all.values()], encounter, abilityTruthReaders({}, encounter, new Set()))
    .then(read => { if (deathSignature(encounter) === signature) deathReaders.set(encounter, { signature, read }); })
    .finally(() => { if (deathFlights.get(encounter)?.loading === loading) deathFlights.delete(encounter); });
  deathFlights.set(encounter, { signature, loading });
  return loading;
}

type Planned = Entry;
type Strip = NonNullable<PlainWalkResult['zone_exits']>[number];
/** One resolved entry: its verdict and the rows it was resolved against (the commit's CAS before-values come from them). */
interface Resolved { verdict: PaoeEntryOnceVerdict; originals: ReadonlyMap<string, Row> }
interface Token {
  encounterId: string; actorId: string; entries: Planned[]; verdicts: Resolved[]; strips: Strip[];
  /** Every member's revision when the verdicts were resolved — then the walk's and each served entry's own bumps (the entry CAS). */
  revs: Map<string, number>;
  /** The foot the walk was cut before (a planned halt), and how to walk the rest when that halt does not land. */
  haltStep?: number; resume?: () => PlainWalkResult | null | Promise<PlainWalkResult | null>;
  /** D-051: a compound follower carried through the zones — never halted, never truncated (Codex review #2 F3). */
  carried: boolean;
  /** Each entry's own draws (Codex review #3 F2: a re-resolution replays them). */
  tapes: RecordedDraw[][];
  /** D-051 chain: where these entries started (the previous entrant's end state, the walk's landings applied), where they end, and the
   *  previous entrant's token (absent = the chain's HEAD, resolved on the page rows). Codex review #3 F1. */
  start: ChainState | null;
  endState?: ChainState;
  after?: Token;
  final?: { x: number; y: number; z: number };
  consent?: PageTriggerConsent;
  replayInputs?: Pick<RunOptions, 'read' | 'records'>;
}
const pending = new Map<string, number>();
const keyOf = (encounterId: string, actorId: string): string => `${upper(encounterId)}|${upper(actorId)}`;

function members(encounter: Row): Map<string, Row> {
  const out = new Map<string, Row>();
  for (const m of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const id = upper(row(row(m)?.encounter_creatures)?.encounter_creature_id);
    if (UUID.test(id)) out.set(id, m as Row);
  }
  return out;
}
const complete = (m: Row | undefined): boolean => !!m && !!row(row(m.computed)?.creature) && !!row(m.item_layer_types);

/** Resolve an owed exit after the landing. Gone rows need no POST; stale answers reclassify against the corrected page. */
async function saveZoneExits(encounterId: string, requested: ZoneStrip[]): Promise<ZoneExitOutcome> {
  let strips = requested;
  let tape: RecordedDraw[] = [];
  const consent = createTriggerConsent(encounterId);
  for (let attempt = 0; attempt < 3; attempt++) {
    const encounter = actorEncounterFor(encounterId) as Row | null;
    if (!encounter) return { kind: 'unknown', error: 'zone-exit:encounter-not-held' };
    const all = members(encounter);
    strips = strips.filter(s => statusRows(all.get(upper(s.targetId)) ?? null, 'target').some(row => upper(row.status_id) === upper(s.statusId)));
    if (!strips.length) return { kind: 'committed', stripped: [] };
    const actorId = upper(strips[0]!.targetId), actor = all.get(actorId)!;
    if (attempt) {
      const x = posCoord(actor, 'x_pos') ?? 0, y = posCoord(actor, 'y_pos') ?? 0;
      const current = resolveWalkZones(encounterId, actorId, [[x, y], [x, y]]);
      if (current.kind !== 'exits' && current.kind !== 'entries') {
        if (current.kind === 'clear') return { kind: 'committed', stripped: [] };
        return { kind: 'refused', status: 409, reason: current.reason };
      }
      const due = new Set(current.strips.map(s => upper(s.statusId)));
      strips = strips.filter(s => due.has(upper(s.statusId)));
      if (!strips.length) return { kind: 'committed', stripped: [] };
    }
    const read = abilityTruthReaders(actor, encounter, new Set([...all].filter(([, m]) => complete(m)).map(([id]) => id)));
    const expectedRevs = revsOf(encounter);
    try {
      const summonsDue = holdsTaggedSummon(all.values());
      if (summonsDue) await ensureDeathReaders(encounter);
      const live = { ...read, triggerConsent: consent, ...(summonsDue ? { deathResources: deathReaders.get(encounter)?.read } : {}) };
      const initial = paoeEntryContext(encounter, all, live, browserRollSource());
      let ctx = initial;
      const removals = strips.flatMap(s => statusRows(all.get(upper(s.targetId)) ?? null, 'target').filter(row => upper(row.status_id) === upper(s.statusId)));
      await hydrateRemovalSupportDefinitions(removals, read,
      async ids => { await Promise.all(ids.map(id => fetchAndCacheObject(id))); });
      const tailIds = exactRemovalTailDefinitionIds(removals, read);
      await Promise.all(tailIds.filter(id => !read.definition(id)).map(id => fetchAndCacheObject(id)));
      const tails = tailIds.map(id => read.definition(id)).filter((definition): definition is Row => definition !== null);
      await hydrateTurnTriggerDefinitions(actor, encounter, tails,
        [...new Set(strips.map(s => upper(s.targetId)))].map(id => all.get(id)!));
      const pinned = pinnedDryEnv();
      let verdict: PaoeEntryVerdict;
      for (;;) {
        let source: ReturnType<typeof recordingRollSource> | undefined;
        try {
          exploreDryPatterns(roll => {
            const dry = paoeEntryContext(initial.encounter, initial.originals, pinned({ ...live, campaignClock: () => live.campaignClock?.() ?? null }), roll);
            dry.dryRun = true;
            return resolveZoneExit(dry, strips);
          }, () => consent.pending().length);
          if (!consent.final && consent.pending().length) { await askTriggerConsent(encounterId, actorId, consent); continue; }
          // Departure can reveal controller consent only on the actual outcome. Replay its draws on a fresh frame after the answer.
          source = recordingRollSource(tapedRollSource(tape, browserRollSource()));
          ctx = paoeEntryContext(initial.encounter, initial.originals, live, source);
          verdict = resolveZoneExit(ctx, strips);
          tape = source.draws;
          break;
        } catch (error) {
          if (!(error instanceof HandBack) || !error.reason.includes(TRIGGER_CONSENT_REASON) || consent.final) throw error;
          if (source) tape = source.draws;
        }
        await askTriggerConsent(encounterId, actorId, consent);
      }
      const anchor = zoneAnchorRow(ctx.encounter, strips[0]!.zoneStatusId);
      const abilityId = upper(anchor?.status_origin_id) || (anchor ? statusItemId(anchor) : '');
      if (!abilityId) return { kind: 'unknown', error: 'zone-exit:anchor-not-held' };
      // Every exact removal, including raw grapple counterparts, carries the page reader's normalized before-value.
      for (const removed of verdict.statusesRemoved) if (!removed.before) {
        const held = statusRows(ctx.originals.get(upper(removed.target)) ?? null, 'target').find(s => upper(s.status_id) === upper(removed.status_id));
        if (held) removed.before = statusRemovalBefore(held, removed.target);
      }
      const saved = await commitZoneEntryVerdict({ encounterId, actorId, abilityId, zoneStatusId: '', verdict, before: ctx.originals,
        expectedRevs, zoneExit: { strips } });
      if (saved.outcome === 'stale') continue;
      if (saved.outcome !== 'served') return saved.outcome === 'error'
        ? { kind: 'unknown', error: saved.reason ?? 'zone-exit:delivery-unavailable' }
        : { kind: 'refused', status: 403, reason: saved.reason ?? 'zone-exit:delivery-refused' };
      const stripped = verdict.statusesRemoved.map(s => upper(s.status_id));
      if (ctx.fortunateOffers?.size) offerFortunate(encounterId, actorId, [...ctx.fortunateOffers]);
      window.dispatchEvent(new CustomEvent('ft:zone-exit-committed', { detail: { encounterId: upper(encounterId), stripped } }));
      return { kind: 'committed', stripped };
    } catch (error) {
      if (error instanceof HandBack || error instanceof Refusal) {
        const reason = error instanceof HandBack ? error.reason : error.message;
        refuseUnported(reason, { encounterId, creatureId: actorId, frame: 'zone-exit' }, undefined, error instanceof Refusal ? 'zone-exit:rule-refused' : undefined);
        return { kind: 'refused', status: 409, reason };
      }
      return { kind: 'unknown', error: String((error as Error)?.message ?? error) };
    }
  }
  return { kind: 'unknown', error: 'zone-exit:stale-retries-exhausted' };
}
registerZoneExitCommitter(saveZoneExits);
/** The member clone stands at the entry foot, including a spatial traversal's elevation. */
function standAt(ctx: JanusCtx, actorId: string, point: [number, number], z: number): void {
  const m = ctx.members.get(upper(actorId));
  if (!m) throw new HandBack('zone-entry:entering-not-held');
  const rev = Number(row(m.encounter_creatures)?.movement_rev ?? 0);
  applyMovementTruth(m as unknown as MovementTruthRow, { x_pos: point[0], y_pos: point[1], z_pos: z, rev });
}
function layerIds(def: Row, key: string): string[] {
  return (ftLayerEntries(def, key) ?? []).map(e => upper(typeof e === 'string' ? e.split('|')[0] : row(e)?.item_id ?? row(row(e)?.items)?.item_id)).filter(id => UUID.test(id));
}
/** Every definition the entry can apply (the ability as a status included, two levels) must be HELD — the planner resolves the frame
 *  synchronously. HALTS are served (the real draw tells the page, before the walk commits, whether it stops a foot short), a speed layer
 *  never re-prices the walk it lands in (the legacy loop reads the speed compiled BEFORE the walk — oracle S1 / S2 == controls C0 / C1),
 *  and a zone's Immobile (Forcecage) now survives the walk: the encumbrance pass manages only its own rows (owner ruling 2026-09-29 — the
 *  old strip was a bug, fixed server-side in ft_encumbrance_remove_own_status, not ported). */
function mayHaltOrReprice(ability: Row, abilityId: string, definition: (id: string) => Row | null): string | null {
  const seen = new Set<string>();
  const visit = (def: Row, id: string, depth: number): string | null => {
    if (seen.has(id)) return null;
    seen.add(id);
    // the loop also halts on a mover's speed <= 0 after the entry (a halting status by id is already a halt): a speed layer that LOWERS (or sets) a speed may reach it, and the page
    // does not recompile the mover mid-walk — hand it back (raises, Graceful Lifts / Blue Road, are served: they never halt)
    if (!HALT_STATUS_IDS.has(upper(id))) for (const key of Object.keys(row(def.item_layer_types) ?? {})) if (/speed/i.test(key)) {
      const lowers = (ftLayerEntries(def, key) ?? []).some(entry => {
        const raw = row(entry) ? row(entry)!.value ?? row(entry)!.layer_value : Array.isArray(entry) ? entry[0] : String(entry).split('|')[0];
        const n = Number(raw);
        return !Number.isFinite(n) || n < 0 || /set|zero|halve/i.test(key);
      });
      if (lowers) return `zone-entry:speed-may-zero:${key}`;
    }
    if (depth >= 2) return null;
    for (const key of [...APPLIED_LAYERS, 'passive_ability']) for (const next of layerIds(def, key)) {
      const nextDef = definition(next);
      if (!nextDef) return `zone-entry:definition-not-held:${next}`;
      const why = visit(nextDef, next, depth + 1);
      if (why) return why;
    }
    return null;
  };
  return visit(ability, abilityId, 0);
}
function warmMembers(encounter: Row): void {
  const hydrate = (window as unknown as { fetchAndCacheCaster?: (id: string, opts: { skipEncounterSeed: true }) => Promise<void> }).fetchAndCacheCaster;
  for (const m of members(encounter).keys()) void hydrate?.(m, { skipEncounterSeed: true })?.catch?.(() => null);
}

/** An entry's status writes, made REAL on the rows the next entry reads (PHP's second janus() reads the first one's saved rows): additions
 *  as rows under a provisional id WRITTEN ONTO THE VERDICT (the commit keeps it; the served answer maps it to the canonical id for the later
 *  verdicts that name it), removals by item, stack changes by row — in every projection that holds the status (Codex review P1, fold F2-F4). */
const projections = (m: Row | undefined): unknown[][] => {
  const st = row(m?.statuses);
  return st ? (['target', 'caster'] as const).map(mode => Array.isArray(st[mode]) ? st[mode] as unknown[] : []) : [];
};
function materialize(ctx: JanusCtx, verdict: PaoeEntryVerdict): void {
  for (const s of verdict.statusesAdded) {
    const sr = s as unknown as Row;
    if (!UUID.test(upper(sr.status_id))) sr.status_id = crypto.randomUUID().toUpperCase();
    const target = ctx.members.get(upper(s.target));
    const entry = { status_id: upper(sr.status_id), status_item_id: upper(s.status_item_id), status_caster_id: upper(s.caster),
      status_target_id: upper(s.target), status_connected_id: upper(s.status_connected_id), status_stack: s.status_stack,
      status_ends_on_target_start_of_turn: s.status_ends_on_target_start_of_turn, status_ends_on_target_end_of_turn: s.status_ends_on_target_end_of_turn,
      status_ends_on_caster_start_of_next_turn: s.status_ends_on_caster_start_of_next_turn, status_ends_on_caster_end_of_next_turn: s.status_ends_on_caster_end_of_next_turn };
    if (target) upsertStatusOnRow(target, entry);
    // the caster's own projection (upsertStatusOnRow writes a foreign caster's side only when it is the row itself)
    const caster = ctx.members.get(upper(s.caster)), cs = caster && caster !== target ? (caster.statuses ??= {}) as Row : null;
    if (cs) cs.caster = [...(Array.isArray(cs.caster) ? cs.caster as unknown[] : []), { ...entry }];
  }
  for (const r of verdict.statusesRemoved) {
    const item = upper(r.status_item_id), target = upper(r.target), id = upper(r.status_id);
    const gone = (e: unknown): boolean => {
      const status = row(e);
      return !!status && (id ? upper(statusScalar(status, 'status_id')) === id
        : statusItemId(status) === item && upper(statusScalar(status, 'status_target_id') ?? target) === target);
    };
    for (const m of ctx.members.values()) {
      const st = row(m.statuses);
      if (st) for (const mode of ['target', 'caster'] as const) if (Array.isArray(st[mode]) && (st[mode] as unknown[]).some(gone))
        st[mode] = (st[mode] as unknown[]).filter(e => !gone(e));
    }
  }
  for (const c of verdict.statusStacks ?? []) {
    const cr = c as unknown as Row;
    for (const m of ctx.members.values()) for (const list of projections(m))
      for (const e of list) { const er = row(e); if (er && upper(er.status_id) === upper(cr.status_id)) er.status_stack = cr.status_stack; }
  }
}
/** Dead or deleted creature rows stop movement. */
function died(v: PaoeEntryVerdict, actorId: string): boolean {
  return Number(ecLean({ encounter_creatures: v.ec[actorId] ?? {} }, 'encounter_creature_dead') ?? 0) === 1
    || !!v.spawnRows?.writes.some(write => write.table === 'encounter_creatures' && write.op === 'update'
      && upper(ecLean({ encounter_creatures: write.row }, 'encounter_creature_id')) === actorId
      && Number(ecLean({ encounter_creatures: write.row }, 'encounter_creature_deleted') ?? 0) === 1);
}
function halts(v: PaoeEntryVerdict, actorId: string): boolean {
  const me = upper(actorId);
  return v.statusesAdded.some(st => upper(st.target) === me && HALT_STATUS_IDS.has(upper(st.status_item_id)))
    || died(v, me);
}
const revsOf = (encounter: Row): Map<string, number> => new Map([...members(encounter)].map(([id, m]) => [id, Number(row(m.encounter_creatures)?.movement_rev ?? 0)]));
/** Run the planned entries in order — each on a FRESH ctx over the rows the previous one left, its status writes made real (its verdict is
 *  then exactly what the shared commit persists) — pushing each; the index of the first that hands back, or null. */
let lastHandBack = '';
/** The rows an entry chain has reached (the members after the earlier entries' writes, made real) — D-051: a carried follower's entries
 *  resolve on the state the mover's (and every earlier follower's) entries left (Codex review #3 F1). */
interface ChainState { all: ReadonlyMap<string, Row>; scene: Row }
interface RunOptions {
  dryRun?: boolean;
  consent?: PageTriggerConsent;
  read?: JanusCtx['read'];
  records?: ReturnType<typeof readPaoeEntryRecords>;
  out?: Resolved[];
  /** Out: each entry's own draws (a re-resolution replays them — Codex review #3 F2). */
  tapes?: RecordedDraw[][];
  /** In: the draws to replay per entry (then `roll` past a mismatch). */
  replay?: ReadonlyArray<ReadonlyArray<RecordedDraw>>;
  /** In / out: where the chain starts, and where it ends once every entry resolved. */
  state?: { current: ChainState | null };
  /** Already served checks in this move survive a later entry's stale-state retry. */
  completedSurfaceRows?: readonly LogRow[];
}
/** Retain only the before-cells this entry will save. Page patches can arrive while earlier
 * entries commit; retaining a mutable whole member would silently change these CAS values. */
function entryBeforeRows(originals: ReadonlyMap<string, Row>, verdict: PaoeEntryVerdict): ReadonlyMap<string, Row> {
  const before = new Map<string, Row>();
  for (const [id, columns] of Object.entries(verdict.ec)) {
    const member = originals.get(upper(id));
    if (!member) continue;
    const ec: Row = {}, position: Row = {};
    for (const key of Object.keys(columns)) {
      if (key === 'x_pos' || key === 'y_pos' || key === 'z_pos') position[key] = posCoord(member, key) ?? 0;
      else {
        const value = ecLean(member, key);
        ec[key] = Array.isArray(value) ? value.slice() : value;
      }
    }
    before.set(upper(id), { encounter_creatures: ec, position });
  }
  return before;
}
function runEntries(encounter: Row, actorId: string, entries: Planned[], z: number, roll: JanusCtx['roll'], opts: RunOptions = {}): number | null {
  const chainOptions = opts;
  // Each step gets a fresh context, so carry resolved surface markers across re-entry before any step is saved.
  const surfaceMarkers: LogRow[] = [];
  let all: ReadonlyMap<string, Row> = opts.state?.current?.all ?? members(encounter);
  let scene: Row = opts.state?.current?.scene ?? encounter;
  const out = opts.out;
  const read = { ...opts.read ?? abilityTruthReaders(all.get(upper(entries[0]!.casterId)) ?? {}, encounter, new Set([...members(encounter)].filter(([, m]) => complete(m)).map(([id]) => id))),
    deathResources: opts.read?.deathResources ?? deathReaders.get(encounter)?.read,
    triggerConsent: opts.consent ?? createTriggerConsent(String(encounterField(encounter, 'encounter_id'))) };
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i]!;
    const source = recordingRollSource(opts.replay ? tapedRollSource(opts.replay[i] ?? [], roll) : roll);
    const ctx = paoeEntryContext(scene, all, read, source);
    ctx.teleportOffers = [];
    ctx.dryRun = opts.dryRun;
    const base = ctx.read;
    try {
      standAt(ctx, actorId, e.point, e.elevation ?? z);
      const originals = ctx.originals;
      const surface = hasSurfaceTypeValue(ctx.read, ctx.read.definition(e.originId));
      const readPaoeEntryRecords = surface
        ? readSurfaceEntryRecords : readLegacyPaoeEntryRecords;
      const baseRecords = surface ? chainOptions.records ?? readPaoeEntryRecords(encounter) : undefined;
      const opts = baseRecords ? { ...chainOptions, records: { ...baseRecords, rows: [...baseRecords.rows, ...surfaceMarkers],
        surfaceEntryMoveRows: [...baseRecords.surfaceEntryMoveRows ?? [], ...surfaceMarkers] } } : chainOptions;
      const v = runPaoeEntryOnce(ctx, { abilityId: e.originId, casterId: e.casterId, enteringId: actorId, statusConnectedId: e.connectedId, forcedExtreme: e.forcedExtreme }, opts.records ?? readPaoeEntryRecords(encounter));
      if (!ctx.dryRun && consentPending(ctx.read)) throw new HandBack('Zone entry needs a player reaction decision before it can settle.');
      materialize(ctx, v);
      if (surface) surfaceMarkers.push(...v.rows.filter(row => row.roll_type_1 === PAOE_ENTRY_RECORD || row.roll_type_1 === 'surface_entry_skipped'));
      finishMovementDecision(ctx);
      out?.push({ verdict: v, originals: entryBeforeRows(originals, v) });
      opts.tapes?.push([...source.draws]);
      all = ctx.members; scene = ctx.encounter;
    } catch (error) {
      if (error instanceof HandBack || error instanceof Refusal) {
        lastHandBack = consentPending(ctx.read) ? 'Zone entry needs a player reaction decision before it can settle.' : error instanceof HandBack ? error.reason : 'refusal';
        if (lastHandBack.startsWith('Death details for ')) deathReaders.delete(encounter);
        return i;
      }
      throw error;
    } finally { ctx.read = base; }
  }
  if (opts.state) opts.state.current = { all, scene };
  return null;
}

/** Hold the action's rows and draw prefix while an actual entry asks a controller. */
async function consentedEntries(encounter: Row, actorId: string, entries: Planned[], z: number,
  opts: Pick<RunOptions, 'state' | 'replay' | 'completedSurfaceRows'> = {}, consent?: PageTriggerConsent): Promise<{
    at: number | null; reason: string; verdicts: Resolved[]; tapes: RecordedDraw[][]; state: ChainState | null;
    start: ChainState; revs: Map<string, number>; consent: PageTriggerConsent; replayInputs: Pick<RunOptions, 'read' | 'records'>;
  }> {
  const deaths = deathReaders.get(encounter);
  const start = opts.state?.current ?? { all: members(encounter), scene: encounter };
  const heldMembers = members(encounter), revs = revsOf(encounter);
  const read = abilityTruthReaders(heldMembers.get(upper(entries[0]!.casterId)) ?? {}, encounter,
    new Set([...heldMembers].filter(([, member]) => complete(member)).map(([id]) => id)));
  const surfaces = entries.map(entry => hasSurfaceTypeValue(read, read.definition(entry.originId)));
  const readPaoeEntryRecords: (scene: Row) => ReturnType<typeof readSurfaceEntryRecords> = surfaces.every(Boolean) ? readSurfaceEntryRecords : surfaces.some(Boolean)
    ? (scene: Row) => {
      return combinePaoeEntryRecords(readLegacyPaoeEntryRecords(scene), readSurfaceEntryRecords(scene));
    } : readLegacyPaoeEntryRecords;
  const clock = read.campaignClock(), records = readPaoeEntryRecords(encounter);
  if (opts.completedSurfaceRows?.length) {
    records.rows = [...records.rows, ...opts.completedSurfaceRows];
    records.surfaceEntryMoveRows = [...records.surfaceEntryMoveRows ?? [], ...opts.completedSurfaceRows];
  }
  const frozenRead = { ...read, deathResources: deaths?.read, campaignClock: () => clock ? { ...clock } : null };
  const rolls = resumableRollSource(browserRollSource());
  const resolve = (answers: PageTriggerConsent) => {
    const verdicts: Resolved[] = [], tapes: RecordedDraw[][] = [], state = { current: start };
    const at = runEntries(encounter, actorId, entries, z, rolls.next(), { out: verdicts, tapes, state, replay: opts.replay, consent: answers, read: frozenRead, records });
    if (at !== null && lastHandBack.startsWith('Death details for ')) deathReaders.delete(encounter);
    return { at, reason: lastHandBack, verdicts, tapes, state: state.current, start, revs, consent: answers,
      replayInputs: { read: frozenRead, records } };
  };
  // A stale re-resolution retains answers from the same entry action, including the bounded ask count.
  if (consent) {
    for (;;) {
      const result = resolve(consent);
      if (result.at === null || consent.final || !consent.pending().length) return result;
      await askTriggerConsent(String(encounterField(encounter, 'encounter_id')), actorId, consent);
    }
  }
  return planWithConsent(String(encounterField(encounter, 'encounter_id')), actorId, resolve,
    result => result.at !== null && result.consent.pending().length > 0);
}

/** Session 3 (compound walks): does this verdict change who is dragged? PHP's death tail releases the dying creature's links
 *  (apply_death_effect.php) before final_creature_move collects the compound, and any link row the entry adds / removes re-shapes it. */
function reshapesCompound(v: PaoeEntryVerdict, actorId: string): boolean {
  const me = upper(actorId);
  return died(v, me)
    || [...v.statusesAdded, ...v.statusesRemoved].some(s => LINK_STATUS_IDS.has(upper((s as unknown as Row).status_item_id)));
}
const LINK_STATUS_IDS = new Set(['FF8D219A-2A83-0EE5-A222-E748753E8D0B', 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40', 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12',
  'DDC66C31-4093-84AF-18BB-093D924B8738', 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD', '0C2A8037-B0BC-8300-C818-198881B00E7D']);

/** One entrant of a compound's entry chain (the mover, then each carried follower — D-051). */
interface Entrant { actorId: string; entries: Planned[]; final: { x: number; y: number; z: number }; compound?: { followerZoneRows: boolean }; carried?: boolean }
/** A chain's start: `after`'s end state (or the page rows), with the walk's members standing at their landings (`moved`). */
function chainStart(encounter: Row, after: Token | undefined, moved: ReadonlyMap<string, { x: number; y: number; z: number }> | undefined): ChainState | null {
  if (!after?.endState && !moved?.size) return null;
  const base = after?.endState ?? { all: members(encounter), scene: encounter };
  if (!moved?.size) return base;
  const view = movementDecisionView(base.scene, base.all);
  const all = view.members;
  for (const [id, at] of moved) {
    const m = all.get(upper(id));
    if (m) applyMovementTruth(m as unknown as MovementTruthRow, { x_pos: at.x, y_pos: at.y, z_pos: at.z, rev: Number(row(m.encounter_creatures)?.movement_rev ?? 0) });
  }
  finishMovementDecision(view);
  return { all, scene: view.encounter };
}
/** Every hand-back of a CHAIN of entrants on synthetic extreme dice, each entrant on the state the earlier ones left under the SAME pattern
 *  (Codex review #3 F1: an earlier entrant's effect — the caster turned invisible — decides a later one's hand-back before any real die).
 *  Also reports the mover's feet a dry run halted on (Codex review #3 F3: the candidate cuts whose carried routes must be proven too). */
function dryChain(encounterId: string, entrants: ReadonlyArray<Entrant>, start: ChainState | null = null):
  { kind: 'handback'; reason: string } | { kind: 'ok'; encounter: Row; planned: Planned[][]; haltSteps: number[] } {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return { kind: 'handback', reason: 'zone-entry:encounter-not-held' };
  const planned: Planned[][] = [];
  for (const entrant of entrants) {
    const checked = staticChecks(encounterId, encounter, entrant.actorId, entrant.entries);
    if (checked.kind === 'handback') return checked;
    planned.push(checked.planned);
  }
  if (!planned.some(p => p.length)) return { kind: 'ok', encounter, planned, haltSteps: [] };
  let first: string | null = null;
  // Session 3 (Codex step-1 review F1/F2): a COMPOUND walk hands back, before any real die, when a dry run can reshape the compound (a death
  // releases links; a link row changes) or can HALT while a follower holds zone rows (the cut stop's strips would take them for good).
  let compoundRisk: string | null = null;
  const haltSteps = new Set<number>();
  try {
    const indexed = entrants.map((entrant, k) => ({ entrant, list: planned[k]! }));
    first = dryRunChain(['max', 'min', 'alt-hi', 'alt-lo'], indexed, start, ({ entrant, list }, pattern, current) => {
      if (!list.length) return { state: current };
      const dry: Resolved[] = [];
      const state = { current };
      if (runEntries(encounter, entrant.actorId, list, entrant.final.z, payloadDryRunRollSource(pattern as 'max' | 'min' | 'alt-hi' | 'alt-lo'), { out: dry, state, dryRun: true }) !== null)
        return { handBack: lastHandBack };
      if (!entrant.carried) dry.forEach((r, i) => { if (halts(r.verdict, entrant.actorId)) haltSteps.add(list[i]!.step); });
      const compound = entrant.compound;
      if (compound && !compoundRisk) {
        if (dry.some(r => reshapesCompound(r.verdict, entrant.actorId))) compoundRisk = 'zone-entry:compound-link-release';
        else if (compound.followerZoneRows && dry.some(r => halts(r.verdict, entrant.actorId))) compoundRisk = 'zone-entry:compound-halt-follower-rows';
      }
      return { state: state.current };
    });
  } catch (error) { return { kind: 'handback', reason: `zone-entry:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` }; }
  if (compoundRisk) return { kind: 'handback', reason: compoundRisk };
  // A frame the page cannot resolve hands the WHOLE walk back, before anything is drawn or saved (owner: no server-rolled fallback).
  if (first !== null) {
    const reason = `zone-entry:frame-not-resolved:${first}`;
    // Older, unrelated gates without an id retain their tree warm-up; named needs retry exactly in preflight below.
    if (/definition-not-held/.test(first) && !missingDefinitionId(reason)) void prefetchZoneDefinitions(encounter);
    return { kind: 'handback', reason };
  }
  return { kind: 'ok', encounter, planned, haltSteps: [...haltSteps].sort((a, b) => a - b) };
}
/** The state-only checks of one entrant's entries (every hand-back that needs no dice). */
function staticChecks(encounterId: string, encounter: Row, actorId: string, entries: Planned[]): { kind: 'handback'; reason: string } | { kind: 'ok'; planned: Planned[] } {
  const all = members(encounter);
  const mover = all.get(upper(actorId));
  const read = abilityTruthReaders(mover ?? {}, encounter, new Set());
  const planned: Planned[] = [];
  for (const e of entries) {
    if (isSelfStatusOrigin(upper(e.zoneStatusId), upper(e.originId))) continue;
    const caster = all.get(upper(e.casterId));
    if (!complete(mover) || !complete(caster)) { warmMembers(encounter); return { kind: 'handback', reason: 'zone-entry:member-compile-not-held' }; }
    const ability = read.definition(e.originId);
    if (!ability) return { kind: 'handback', reason: `zone-entry:definition-not-held:${e.originId}` };
    const why = mayHaltOrReprice(ability, upper(e.originId), read.definition);
    if (why) {
      return { kind: 'handback', reason: why };
    }
    planned.push({ ...e });
  }
  return { kind: 'ok', planned };
}

const planner: ZoneEntryPlanner = {
  prepare(encounterId, actorId, entries) {
    const encounter = actorEncounterFor(encounterId) as Row | null;
    if (!encounter) return null;
    const read = abilityTruthReaders({}, encounter, new Set());
    const surfaceOnly = entries?.length ? entries.every(entry => {
      const definition = read.definition(entry.originId);
      return hasSurfaceTypeValue(read, definition);
    }) : undefined;
    const records = ensurePaoeEntryRecords(encounter, actorId, surfaceOnly), deaths = ensureDeathReaders(encounter);
    return records || deaths ? Promise.all([records, deaths]).then(() => undefined) : null;
  },
  preflight(encounterId, entrants) {
    const dry = dryChain(encounterId, entrants.map(e => ({ ...e, entries: e.entries.map(x => ({ ...x })) })));
    const missing = dry.kind === 'handback' ? missingDefinitionId(dry.reason) : undefined;
    if (missing) throw new ZoneRecordsNeeded(fetchAndCacheObject(missing).then(() => {
      const encounter = actorEncounterFor(encounterId) as Row | null;
      // A failed/epoch-discarded fetch cannot loop indefinitely through movement's preparation retry.
      if (!encounter || !abilityTruthReaders({}, encounter, new Set()).definition(missing))
        throw new HandBack(`zone-entry:definition-not-held:${missing}`);
    }));
    return dry.kind === 'handback' ? { reason: dry.reason, haltSteps: [] } : { reason: null, haltSteps: dry.haltSteps };
  },
  async plan(encounterId, actorId, entries, final, strips, compound, carried, after, moved) {
    const prior = after as Token | undefined;
    // the previous entrant's rows could not be re-derived (a truncated plan whose replay failed): never resolve on rows missing its effects
    if (prior && prior.entries.length && !prior.endState) return { kind: 'handback', reason: 'zone-entry:chain-state-not-held' };
    const encounterNow = actorEncounterFor(encounterId) as Row | null;
    const start = encounterNow ? chainStart(encounterNow, prior, moved) : null;
    const dry = dryChain(encounterId, [{ actorId, entries, final, compound, carried }], start);
    if (dry.kind === 'handback') return dry;
    const { encounter } = dry;
    const planned = dry.planned[0]!;
    const base = { encounterId: upper(encounterId), actorId: upper(actorId), strips: strips.map(s => ({ ...s })), revs: revsOf(encounter), carried: !!carried,
      tapes: [] as RecordedDraw[][], start, ...(prior ? { after: prior } : {}) };
    if (!planned.length) return { kind: 'planned', token: { ...base, entries: [], verdicts: [], endState: start ?? undefined } satisfies Token };
    // THE REAL DRAW, here — BEFORE the walk commits (Codex S2 review P1: four extreme patterns do not cover every outcome, e.g. a contest the
    // caster wins only on mid rolls). A residual the dry runs missed still hands the whole walk back (the legacy walk then resolves the
    // entry and its halts) — the ability lane's accepted residual bias — rather than falling to a server re-roll after the walk landed.
    // D-051: a follower's real draw starts where the mover's (and every earlier follower's) REAL verdicts left the rows.
    let resolved: Awaited<ReturnType<typeof consentedEntries>>;
    try {
      resolved = await consentedEntries(encounter, actorId, planned, final.z, { state: { current: start } });
      if (resolved.at !== null) return { kind: 'handback', reason: `zone-entry:real-draw-residual:${resolved.reason}` };
      // the real draw found what no dry run did (the accepted real-draw residual class): still never commit a reshaped / follower-stripping compound
      if (compound && resolved.verdicts.some(r => reshapesCompound(r.verdict, actorId) || (compound.followerZoneRows && halts(r.verdict, actorId))))
        return { kind: 'handback', reason: 'zone-entry:real-draw-residual:compound' };
    } catch (error) { return { kind: 'handback', reason: `zone-entry:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` }; }
    // A HALT (move_encounter_creature_logic: after the entries at a foot, a mover now Dead / Prone / Restrained / Unconscious / Grappled /
    // Paralyzed / Stunned stops, and the step is RESTORED — it ends on the previous foot): the first foot whose verdicts land one on the mover.
    // a death halts too: a creature's lethal hit ends in the death tail / the dead flag, not a Dead row in the verdict (Codex review P2)
    // D-051: a CARRIED follower is not walking — nothing it is given stops it (it keeps its landing; the halting status stands).
    const { verdicts, tapes, state, consent } = resolved;
    const at = carried ? -1 : verdicts.findIndex(r => halts(r.verdict, actorId));
    const haltStep = at >= 0 ? planned[at]!.step : undefined;
    return { kind: 'planned', haltStep, token: { ...base, start: resolved.start, entries: planned, verdicts, tapes, endState: state ?? undefined,
      consent, replayInputs: resolved.replayInputs, final: { ...final } } satisfies Token };
  },
  truncate(token, haltStep, strips, resume) {
    // The walk now ends on the foot BEFORE haltStep: entries past the halting foot never happen; every entry AT it still resolves (all the
    // zones entered at one contact do) and — the mover ending outside them — is swept at the end.
    const t = token as Token;
    const keep = t.entries.map((e, i) => [e, i] as const).filter(([e]) => e.step <= haltStep);
    const trimmed = keep.length !== t.entries.length;
    t.entries = keep.map(([e]) => ({ ...e, exitsAfter: e.step === haltStep ? true : e.exitsAfter }));
    t.verdicts = keep.map(([, i]) => t.verdicts[i]!);
    t.tapes = keep.map(([, i]) => t.tapes[i] ?? []);
    t.strips = strips.map(s => ({ ...s }));
    t.haltStep = haltStep; t.resume = resume;
    // D-051: the followers chain on what the KEPT entries leave — replayed from their own tapes (the same draws, so the same verdicts)
    if (trimmed) t.endState = replayEndState(t) ?? undefined;
  },
  hold(token) {
    const t = token as Token;
    const key = keyOf(t.encounterId, t.actorId);
    pending.set(key, (pending.get(key) ?? 0) + 1);
  },
  drop(token) { release(token as Token); },
  commit(token, rev, final, confirmed) {
    const t = token as Token;
    // D-051 (Codex review #2 F1, #4 F1–F4): the entering creature takes its saved landing revision. The chain's HEAD keeps its verdicts (the
    // page rows they were resolved on changed only by the walk); a LATER entrant's verdicts were resolved on MODELLED rows, so they are always
    // resolved again — on their own tapes, on the page rows once those show what the earlier entrants confirmed, minus the rows their sweeps
    // stripped (a strip reaches the page only by a later push) — before anything is sent.
    const participants = new Set(t.verdicts.flatMap(r => verdictParticipants(t.encounterId, t.actorId, r.verdict)));
    const adopted = adoptChainRevs(t.revs, t.actorId, rev, participants, confirmed ?? null, !t.after);
    t.revs = adopted.revs;
    const bumped = new Set<string>(), stripped = new Set<string>();
    return commitEntries(t, final, adopted.refresh, bumped, confirmed?.stripped ?? new Set(), stripped).then(resume => {
      release(t);
      if (resume) resumeWalk(t, final);
      return { revs: new Map(t.revs), touched: bumped, ...(stripped.size ? { stripped } : {}) } satisfies ChainRevs;
    }, () => { release(t); return null; });
  },
  pending(encounterId, actorId) { return (pending.get(keyOf(encounterId, actorId)) ?? 0) > 0; },
};
function release(t: Token): void {
  const key = keyOf(t.encounterId, t.actorId);
  const n = (pending.get(key) ?? 0) - 1;
  if (n > 0) pending.set(key, n); else pending.delete(key);
}

/** A planned halt did not land: walk the rest of the requested route from the stop (after this walk's barriers settle) — bound to the state
 *  this walk left: the actor still at the stop, its revision unchanged since the first attempt; anything else moved it and the continuation is
 *  void (Codex fold review #3 F6). */
function resumeWalk(t: Token, stop: { x: number; y: number }): void {
  const state = (): { rev: number; x: number; y: number } | null => {
    const m = members(row(actorEncounterFor(t.encounterId)) ?? {}).get(t.actorId), ec = row(m?.encounter_creatures), pos = row(m?.position);
    return ec ? { rev: Number(ec.movement_rev ?? 0), x: Number(pos?.x_pos ?? ec.x_pos ?? NaN), y: Number(pos?.y_pos ?? ec.y_pos ?? NaN) } : null;
  };
  let bound: number | null = null;
  const attempt = async (n: number): Promise<void> => {
    const now = state();
    const at = !!now && Math.abs(now.x - stop.x) < 1e-3 && Math.abs(now.y - stop.y) < 1e-3;
    // a late snapshot / caster push can show the row behind the stop for a moment: wait for it to settle there (bounded) before binding
    if (!at && bound === null && n < 8) { setTimeout(() => attempt(n + 1), 750 * (n + 1)); return; }
    if (!now || !at || (bound !== null && now.rev !== bound)) {
      window.dispatchEvent(new CustomEvent('ft:walk-zone-entry-resume', { detail: { encounterId: t.encounterId, actorId: t.actorId, attempts: n, served: false, superseded: true } }));
      return;
    }
    bound ??= now.rev;
    let walk: PlainWalkResult | null = null;
    try { walk = await t.resume?.() ?? null; }
    catch (error) { if (error instanceof MovementRefused && n < 8) { setTimeout(() => attempt(n + 1), 750 * (n + 1)); return; } }
    window.dispatchEvent(new CustomEvent('ft:walk-zone-entry-resume', { detail: { encounterId: t.encounterId, actorId: t.actorId, attempts: n + 1, served: !!walk } }));
  };
  attempt(0);
}
/** A stale answer means SQL moved past the rows this page holds: wait (bounded) for the correction to reach them before resolving again, so
 *  the re-resolution is not made on the same stale rows (Codex fold review #3 F1). */
async function rowsCaughtUp(t: Token, ids: readonly string[]): Promise<boolean> {
  for (let n = 0; n < 20; n++) {
    const all = members(row(actorEncounterFor(t.encounterId)) ?? {});
    if (ids.some(id => Number(row(all.get(id)?.encounter_creatures)?.movement_rev ?? 0) !== (t.revs.get(id) ?? -1))) return true;
    await new Promise(resolve => setTimeout(resolve, 250));
  }
  return false;
}
/** D-051: wait (bounded) until the page holds every listed member at (or past) the revision an earlier commit of the compound confirmed. */
async function rowsAt(t: Token, target: ReadonlyMap<string, number>): Promise<boolean> {
  for (let n = 0; n < 20; n++) {
    const all = members(row(actorEncounterFor(t.encounterId)) ?? {});
    if ([...target].every(([id, rev]) => Number(row(all.get(upper(id))?.encounter_creatures)?.movement_rev ?? -1) >= rev)) return true;
    await new Promise(resolve => setTimeout(resolve, 250));
  }
  return false;
}
/** Commit the entries and report whether the planned halt failed to land. */
async function commitEntries(t: Token, final: { x: number; y: number; z: number }, refresh: ReadonlyMap<string, number>, bumped: Set<string>,
  exclude: ReadonlySet<string>, strippedOut: Set<string>): Promise<boolean> {
  const outcomes: Row[] = [];
  let reresolved = false, halted = false, drift = false, unresolvedHalt = false;
  /** provisional status id → the canonical id the served commit minted (a later chained verdict may name it) */
  const canonical = new Map<string, string>();
  const completedSurfaceRows: LogRow[] = [];
  const remap = (v: PaoeEntryVerdict): void => {
    for (const list of [v.statusStacks ?? [], v.statusesRemoved] as unknown[][]) for (const e of list) {
      const er = row(e), id = upper(er?.status_id);
      if (er && canonical.has(id)) er.status_id = canonical.get(id);
    }
  };
  // Resolve entries FROM index i again on the rows the page holds now (a fresh draw): after a stale / refused answer, or after an entry that
  // did not land (its provisional effects were read by the later verdicts — Codex fold review #3 F2). A fresh draw that stops the mover
  // EARLIER than the walk went drops the entries past that foot (legacy ends there; the position is not walked back — a foreign write raced
  // this walk's entries: named residual, reported as drift). False when the page cannot resolve them (the later entries are not sent).
  const reresolveFrom = async (i: number, stale: boolean): Promise<boolean> => {
    // no participant row moved within the wait: the page still holds the rows SQL refused — resolving again would repeat the stale draw
    const caughtUp = !stale || await rowsCaughtUp(t, [t.actorId, ...new Set(t.entries.slice(i).map(x => upper(x.casterId)))]);
    const encounter = actorEncounterFor(t.encounterId) as Row | null;
    const fresh: Resolved[] = [];
    const freshTapes: RecordedDraw[][] = [];
    rerun = true;
    if (encounter) {
      const read = abilityTruthReaders({}, encounter, new Set());
      const surfaceOnly = t.entries.slice(i).every(entry => {
        const definition = read.definition(entry.originId);
        return hasSurfaceTypeValue(read, definition);
      });
      try { await Promise.all([ensurePaoeEntryRecords(encounter, t.actorId, surfaceOnly), ensureDeathReaders(encounter)]); }
      catch { return false; }
    }
    // Codex review #3 F2: the entries' OWN draws are replayed first — a draw the page made is never re-rolled because the state moved; only
    // a resolution that now asks for different dice draws fresh past that point
    // the page rows, minus what an earlier entrant's committed sweep stripped (not on the page until its push lands)
    const pageState = encounter && exclude.size ? { current: withoutStatuses(encounter, exclude) } : undefined;
    const resolved = caughtUp && encounter ? await consentedEntries(encounter, t.actorId, t.entries.slice(i), final.z,
      { replay: t.tapes.slice(i), completedSurfaceRows, ...(pageState ? { state: pageState } : {}) }, t.consent) : null;
    if (!resolved || resolved.at !== null) {
      t.entries = t.entries.slice(0, i); t.verdicts = t.verdicts.slice(0, i); t.tapes = t.tapes.slice(0, i);
      // entries left unresolved: a planned halt among them can no longer be shown not to land — the walk does not resume past it
      if (t.haltStep !== undefined) unresolvedHalt = true;
      return false;
    }
    fresh.push(...resolved.verdicts); freshTapes.push(...resolved.tapes); t.consent = resolved.consent; t.replayInputs = resolved.replayInputs;
    t.verdicts.splice(i, fresh.length, ...fresh);
    t.tapes.splice(i, freshTapes.length, ...freshTapes);
    t.revs = resolved.revs;
    const k = fresh.findIndex(r => halts(r.verdict, t.actorId));
    // D-051 (Codex review #2 F3): a CARRIED follower keeps every entry of its path — it is not walking, a halt stops nothing
    const survivors = survivorsAfterReresolve(t.entries.map(x => x.step), i, k >= 0 ? t.entries[i + k]!.step : undefined, t.haltStep, t.carried);
    if (survivors.drift) {
      t.verdicts = survivors.keep.map(j => t.verdicts[j]!); t.entries = survivors.keep.map(j => t.entries[j]!); t.tapes = survivors.keep.map(j => t.tapes[j] ?? []);
      drift = true;
    }
    return true;
  };
  let rerun = false;
  const plannedCount = t.entries.length;
  // The planner is called from INSIDE the walk's accepted handler, before the row carries the walk's revision: the first entry's arrival
  // revision (its expected_rev and its derived operation id) is read only once the walk has left the outbox.
  try { await waitForActorMovement(t.encounterId, t.actorId); } catch { /* a stuck outbox is reported by its own lane */ }
  // D-051 (Codex review #2 F1, #4 F1–F4): a LATER entrant of the chain resolves again before its first send, on the page rows once they show
  // what the earlier entrants confirmed (bounded wait). Rows that never catch up are resolved anyway — their stale revisions make the CAS
  // answer stale, and the ordinary single re-resolution below waits for them; a modelled verdict is never sent.
  if (refresh.size && t.entries.length) {
    await rowsAt(t, refresh);
    if (!(await reresolveFrom(0, false))) unresolvedHalt ||= t.haltStep !== undefined;
  }
  for (let i = 0; i < t.entries.length; i++) {
    const e = t.entries[i]!;
    const send = (r: Resolved): ReturnType<typeof commitZoneEntryVerdict> => {
      if (r.verdict.surface_entry_skipped && !r.verdict.rows.length) return Promise.resolve({ outcome: 'served', statusesAdded: [], revs: {} });
      remap(r.verdict); return commitZoneEntryVerdict({ encounterId: t.encounterId, actorId: t.actorId, abilityId: e.originId, zoneStatusId: e.zoneStatusId,
      verdict: r.verdict, before: r.originals, expectedRevs: t.revs }); };
    let answer = await send(t.verdicts[i]!);
    // Refused on stale state (another write landed first): the entry is still owed, and the page — never PHP — resolves it again on the rows
    // it holds NOW (a fresh draw on a genuinely new state), this entry and every later one, once.
    if ((answer.outcome === 'stale' || answer.outcome === 'refused') && !reresolved) {
      reresolved = true;
      if (await reresolveFrom(i, answer.outcome === 'stale') && i < t.entries.length) answer = await send(t.verdicts[i]!);
    }
    outcomes.push({ zone: e.zoneStatusId, outcome: answer.outcome, reresolved });
    if (answer.outcome === 'served') {
      completedSurfaceRows.push(...t.verdicts[i]!.verdict.rows.filter(row => row.roll_type_1 === PAOE_ENTRY_RECORD || row.roll_type_1 === 'surface_entry_skipped'));
      const scene = actorEncounterFor(t.encounterId) as Row | null;
      if (scene) adoptPaoeEntryRecords(scene, t.verdicts[i]!.verdict.rows);
      for (const [id, rev] of Object.entries(answer.revs)) { t.revs.set(upper(id), rev); bumped.add(upper(id)); }
      t.verdicts[i]!.verdict.statusesAdded.forEach((s, j) => {
        const from = upper((s as unknown as Row).status_id), to = upper((answer.statusesAdded[j] as unknown as Row | undefined)?.status_id);
        if (UUID.test(from) && UUID.test(to) && from !== to) canonical.set(from, to);
      });
      if (halts(t.verdicts[i]!.verdict, t.actorId)) halted = true;
    }
    // Only a refusal on the zone's own state proves the entry does not happen and lets the walk go on
    // past a planned halt — a transport error or an unresolved stale is an obligation still owed, never a bypass (Codex fold review #3 F4).
    if (t.haltStep !== undefined && e.step === t.haltStep && (answer.outcome === 'error' || answer.outcome === 'stale')) unresolvedHalt = true;
    // the entry bumped the walker's revision: let the outbox install it on the row before the next entry / walk reads it
    try { await waitForActorMovement(t.encounterId, t.actorId); } catch { /* a stuck outbox is reported by its own lane */ }
    if (answer.outcome !== 'served') {
      // it did not land: the later verdicts read its provisional effects — resolve them again without it
      if (i + 1 < t.entries.length && !(await reresolveFrom(i + 1, false))) unresolvedHalt ||= t.haltStep !== undefined;
      continue;
    }
    // A WALK-THROUGH: the mover ends outside this zone, so the end-of-move sweep strips what the entry gave it on the zone's connection; the
    // zone's CASTER standing outside its zone loses what the entry gave IT (Moonbeam, Cloudkill …) — the legacy level-triggered sweep.
    const casterOutside = upper(e.casterId) !== upper(t.actorId) && creatureStandsInZone(t.encounterId, e.casterId, e.zoneStatusId) === false;
    for (const st of answer.statusesAdded) {
      const target = upper(st.target), statusId = upper((st as unknown as Row).status_id);
      if (upper(st.status_connected_id) !== upper(e.connectedId) || upper(st.status_item_id) === 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B') continue;
      const strip = (target === upper(t.actorId) && e.exitsAfter) || (target === upper(e.casterId) && casterOutside);
      if (strip && UUID.test(statusId) && !t.strips.some(x => upper(x.statusId) === statusId))
        t.strips.push({ statusId, targetId: target, zoneStatusId: e.zoneStatusId, connectedId: e.connectedId });
    }
  }
  // The end-of-move sweeps follow the entries; the browser resolves their lifecycle and the ability commit saves it.
  let exits: Row | null = null;
  const batches: Row[] = [];
  // An exit frame takes at most 64 strips; a walk-through can exceed that limit, so commit in batches.
  for (let from = 0; from < t.strips.length; from += 64) {
    const batch = t.strips.slice(from, from + 64);
    let outcome: Awaited<ReturnType<typeof commitZoneExits>> | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      outcome = await commitZoneExits(t.encounterId, batch);
      if (outcome.kind !== 'unknown') break;
      await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
    }
    batches.push(outcome as unknown as Row);
    // the ordinary exit path's settle barrier: the actor's next walk waits until the page no longer holds the stripped rows
    // every CLAIMED row (a retry may report it lready stripped): each releases once the page no longer shows it, or at the deadline
    if (outcome?.kind === 'committed') { holdCommittedZoneStrips(t.encounterId, t.actorId, batch.map(s => s.statusId)); for (const x of batch) strippedOut.add(upper(x.statusId)); }
  }
  exits = batches.length ? { batches } : null;
  // A planned halt that did not land (every halting-foot entry skipped / refused, or re-resolved without a halt): the walk goes on — and
  const resume = t.haltStep !== undefined && !halted && !drift && !unresolvedHalt && !!t.resume;
  window.dispatchEvent(new CustomEvent('ft:walk-zone-entries', { detail: { encounterId: t.encounterId, actorId: t.actorId, outcomes, exits, drift, resume: !!resume, carried: t.carried,
    reresolved: rerun, planned: plannedCount } }));
  return resume;
}
/** Remove only matching status projections; every unaffected member stays shared. */
function withoutStatuses(encounter: Row, ids: ReadonlySet<string>): ChainState {
  const view = movementDecisionView(encounter, members(encounter));
  const gone = (e: unknown): boolean => ids.has(upper(row(e)?.status_id));
  for (const member of view.members.values()) {
    const st = row(member.statuses);
    if (st) for (const mode of ['target', 'caster'] as const) if (Array.isArray(st[mode]) && (st[mode] as unknown[]).some(gone))
      st[mode] = (st[mode] as unknown[]).filter(e => !gone(e));
  }
  finishMovementDecision(view);
  return { all: view.members, scene: view.encounter };
}
/** The rows a token's (kept) entries leave, re-run on their own tapes from the token's chain start — the same draws give the same verdicts. */
function replayEndState(t: Token): ChainState | null {
  if (!t.entries.length) return t.start;
  const encounter = actorEncounterFor(t.encounterId) as Row | null;
  if (!encounter || !t.final) return null;
  const state = { current: t.start };
  try {
    const at = runEntries(encounter, t.actorId, t.entries, t.final.z, browserRollSource(), { replay: t.tapes, state, consent: t.consent, ...t.replayInputs });
    return at === null ? state.current : null;
  } catch { return null; }
}

registerZoneEntryPlanner(planner);

// The synchronous walk planner and this prefetch share the page's held definitions.
// Warm only missing zone origins and their payloads (two levels) when rows arrive.
const inFlight = new Map<string, Promise<unknown>>();
async function prefetchZoneDefinitions(encounter: Row | null): Promise<void> {
  if (!encounter) return;
  const anchors: Row[] = [];
  const collect = (holder: Row | null): void => {
    for (const s of Array.isArray(row(holder?.statuses)?.target) ? row(holder?.statuses)!.target as unknown[] : []) {
      const r = row(s);
      if (r && Number(r.status_aoe_source ?? 0) === 1) anchors.push(r);
    }
  };
  collect(encounter);
  for (const m of members(encounter).values()) collect(m);
  let frontier = anchors.filter(a => !isSelfStatusOrigin(upper(a.status_id), upper(a.status_origin_id)))
    .map(a => upper(a.status_origin_id || a.status_item_id)).filter(id => UUID.test(id));
  const actorId = [...members(encounter).keys()].find(id => id === upper(window.ftGetActiveCasterId?.()));
  const definition = abilityTruthReaders({}, encounter, new Set()).definition;
  for (let depth = 0; depth < 3 && frontier.length; depth++) {
    const ids = [...new Set(frontier)];
    await Promise.all(ids.map(id => {
      if (definition(id)) return null;
      let flight = inFlight.get(id);
      if (!flight) { flight = fetchAndCacheObject(id).catch(() => null).finally(() => inFlight.delete(id)); inFlight.set(id, flight); }
      return flight;
    }));
    // what the frame reads beyond the zone's own row: the statuses it applies, their passives, the damage types it rolls
    frontier = ids.flatMap(id => { const def = definition(id); return def ? [...APPLIED_LAYERS, 'passive_ability', 'damage_type', 'if_target_has_status_effect_active'].flatMap(k => layerIds(def, k)) : []; });
  }
  // Decide surface presence only after its held definition arrived. Surface entry reads the delivered log, never the legacy history query.
  if (anchors.length && actorId) await ensurePaoeEntryRecords(encounter, actorId, onlySurfaceEntryZones(encounter))?.catch(() => null);
}
// Every encounter event (a zone can be placed / a status patched in at any time; the walk may come before the next snapshot), plus the rows
// already cached when this bundle loads and two late sweeps (the initial snapshot can land after both). In-flight fetches are shared.
const sweep = (): void => { try { for (const encounter of window.FateTide?.encounterCache?.values() ?? []) void prefetchZoneDefinitions(row(encounter)); } catch { /* cache not ready */ } };
for (const name of ['ft:encounter-state-received', 'ft:encounter-installed', 'ft:encounter-rows-patched'])
  window.addEventListener(name, event => { const enc = row((event as CustomEvent).detail?.encounterArr); if (enc) { forgetPaoeEntryRecords(enc); void prefetchZoneDefinitions(enc); } else sweep(); });
sweep();
setTimeout(sweep, 2000);
setTimeout(sweep, 8000);
Object.assign(window, { ftZoneEntryPrefetch: (encounterId: string) => prefetchZoneDefinitions(actorEncounterFor(upper(encounterId)) as Row | null) });
